/**
 * @license
 * Copyright (c) 2021 Handsoncode. All rights reserved.
 */
import { AbsoluteCellRange } from './AbsoluteCellRange';
import { CellError, ErrorType, simpleCellAddress } from './Cell';
import { ErrorMessage } from './error-message';
import { AstNodeType } from './parser';
export class MatrixSize {
    constructor(width, height) {
        this.width = width;
        this.height = height;
        if (width <= 0 || height <= 0) {
            throw Error('Incorrect matrix size');
        }
    }
}
export function matrixSizeForTranspose(inputSize) {
    return new MatrixSize(inputSize.height, inputSize.width);
}
export function matrixSizeForMultiplication(leftMatrixSize, rightMatrixSize) {
    return new MatrixSize(rightMatrixSize.width, leftMatrixSize.height);
}
export function matrixSizeForPoolFunction(inputMatrix, windowSize, stride) {
    return new MatrixSize(1 + (inputMatrix.width - windowSize) / stride, 1 + (inputMatrix.height - windowSize) / stride);
}
export function checkMatrixSize(ast, formulaAddress) {
    if (ast.type === AstNodeType.FUNCTION_CALL) {
        switch (ast.procedureName) {
            case 'MMULT': {
                if (ast.args.length !== 2) {
                    return new CellError(ErrorType.NA, ErrorMessage.WrongArgNumber);
                }
                if (ast.args.some((astIt) => astIt.type === AstNodeType.EMPTY)) {
                    return new CellError(ErrorType.NUM, ErrorMessage.EmptyArg);
                }
                const left = checkMatrixSize(ast.args[0], formulaAddress);
                const right = checkMatrixSize(ast.args[1], formulaAddress);
                if (left instanceof CellError) {
                    return left;
                }
                else if (right instanceof CellError) {
                    return right;
                }
                else if (left.width !== right.height) {
                    return new CellError(ErrorType.VALUE, ErrorMessage.MatrixDimensions);
                }
                else {
                    return matrixSizeForMultiplication(left, right);
                }
            }
            case 'MEDIANPOOL':
            case 'MAXPOOL': {
                if (ast.args.length < 2 || ast.args.length > 3) {
                    return new CellError(ErrorType.NA, ErrorMessage.WrongArgNumber);
                }
                if (ast.args.some((astIt) => astIt.type === AstNodeType.EMPTY)) {
                    return new CellError(ErrorType.NUM, ErrorMessage.EmptyArg);
                }
                const matrix = checkMatrixSize(ast.args[0], formulaAddress);
                const windowArg = ast.args[1];
                if (matrix instanceof CellError) {
                    return matrix;
                }
                else if (windowArg.type !== AstNodeType.NUMBER) {
                    return new CellError(ErrorType.VALUE, ErrorMessage.NumberExpected);
                }
                const window = windowArg.value;
                let stride = windowArg.value;
                if (ast.args.length === 3) {
                    const strideArg = ast.args[2];
                    if (strideArg.type === AstNodeType.NUMBER) {
                        stride = strideArg.value;
                    }
                    else {
                        return new CellError(ErrorType.VALUE, ErrorMessage.MatrixParams);
                    }
                }
                if (window > matrix.width || window > matrix.height
                    || stride > window
                    || (matrix.width - window) % stride !== 0 || (matrix.height - window) % stride !== 0) {
                    return new CellError(ErrorType.VALUE); //TODO
                }
                return matrixSizeForPoolFunction(matrix, window, stride);
            }
            case 'TRANSPOSE': {
                if (ast.args.length !== 1) {
                    return new CellError(ErrorType.NA, ErrorMessage.WrongArgNumber);
                }
                if (ast.args[0].type === AstNodeType.EMPTY) {
                    return new CellError(ErrorType.NUM, ErrorMessage.EmptyArg);
                }
                const size = checkMatrixSize(ast.args[0], formulaAddress);
                return size instanceof CellError ? size : matrixSizeForTranspose(size);
            }
            default: {
                return new CellError(ErrorType.VALUE, ErrorMessage.MatrixFunction);
            }
        }
    }
    else if (ast.type === AstNodeType.CELL_RANGE) {
        const range = AbsoluteCellRange.fromCellRange(ast, formulaAddress);
        return { width: range.width(), height: range.height() };
    }
    else if (ast.type === AstNodeType.NUMBER || ast.type === AstNodeType.CELL_REFERENCE) {
        return { width: 1, height: 1 };
    }
    else {
        return new CellError(ErrorType.VALUE); //TODO
    }
}
export class NotComputedMatrix {
    constructor(size) {
        this.size = size;
    }
    width() {
        return this.size.width;
    }
    height() {
        return this.size.height;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    get(col, row) {
        throw Error('Matrix not computed yet.');
    }
}
export class Matrix {
    constructor(matrix) {
        this.size = new MatrixSize(matrix.length > 0 ? matrix[0].length : 0, matrix.length);
        this.matrix = matrix;
    }
    addRows(aboveRow, numberOfRows) {
        this.matrix.splice(aboveRow, 0, ...this.zeroArrays(numberOfRows, this.width()));
        this.size.height += numberOfRows;
    }
    addColumns(aboveColumn, numberOfColumns) {
        for (let i = 0; i < this.height(); i++) {
            this.matrix[i].splice(aboveColumn, 0, ...new Array(numberOfColumns).fill(0));
        }
        this.size.width += numberOfColumns;
    }
    removeRows(startRow, endRow) {
        if (this.outOfBound(0, startRow) || this.outOfBound(0, endRow)) {
            throw Error('Matrix index out of bound');
        }
        const numberOfRows = endRow - startRow + 1;
        this.matrix.splice(startRow, numberOfRows);
        this.size.height -= numberOfRows;
    }
    removeColumns(leftmostColumn, rightmostColumn) {
        if (this.outOfBound(leftmostColumn, 0) || this.outOfBound(rightmostColumn, 0)) {
            throw Error('Matrix index out of bound');
        }
        const numberOfColumns = rightmostColumn - leftmostColumn + 1;
        for (const row of this.matrix) {
            row.splice(leftmostColumn, numberOfColumns);
        }
        this.size.width -= numberOfColumns;
    }
    zeroArrays(count, size) {
        const result = [];
        for (let i = 0; i < count; ++i) {
            result.push(new Array(size).fill(0));
        }
        return result;
    }
    get(col, row) {
        if (this.outOfBound(col, row)) {
            throw Error('Matrix index out of bound');
        }
        return this.matrix[row][col];
    }
    set(col, row, value) {
        if (this.outOfBound(col, row)) {
            throw Error('Matrix index out of bound');
        }
        this.matrix[row][col] = value;
    }
    width() {
        return this.size.width;
    }
    height() {
        return this.size.height;
    }
    raw() {
        return this.matrix;
    }
    *generateValues(leftCorner) {
        for (let row = 0; row < this.size.height; ++row) {
            for (let col = 0; col < this.size.width; ++col) {
                yield [this.matrix[row][col], simpleCellAddress(leftCorner.sheet, leftCorner.col + col, leftCorner.row + row)];
            }
        }
    }
    outOfBound(col, row) {
        return col < 0 || row < 0 || row > this.size.height - 1 || col > this.size.width - 1;
    }
}
export class ErroredMatrix {
    constructor(error, size) {
        this.error = error;
        this.size = size;
    }
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    get(col, row) {
        return this.error;
    }
    width() {
        return this.size.width;
    }
    height() {
        return this.size.height;
    }
}
